import { ChevronLeft, ChevronRight, Download, ExternalLink, File, Trash2, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import type { FileBody } from '@/shared/api/generated/model';
import { cx } from '@/shared/lib/cx';
import { fileKind, fileLabel, fileUrl, formatSize } from '@/shared/lib/files';
import { durations, exitEase, springs } from '@/shared/lib/motion';
import { t } from '@/shared/i18n';
import { Tooltip } from '@/shared/ui/Tooltip';

import styles from './FileViewer.module.scss';
import { closeViewer, dropFromViewer, stepViewer, useViewer } from './viewerStore';

/** Больше этого текст не показываем — читать такое в окне всё равно нельзя. */
const MAX_TEXT = 1024 * 1024;

/**
 * Просмотрщик вложений: один оверлей на приложение. Картинки, видео,
 * аудио и PDF браузер рисует сам; текст и код показываем моноширинным;
 * остальное — карточкой с типом, размером и скачиванием. Стрелки листают
 * все файлы задачи, Esc закрывает.
 */
export function FileViewerHost() {
  const viewer = useViewer();

  useEffect(() => {
    if (viewer === null) {
      return;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeViewer();
      } else if (event.key === 'ArrowLeft') {
        stepViewer(-1);
      } else if (event.key === 'ArrowRight') {
        stepViewer(1);
      }
    };

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [viewer]);

  const file = viewer?.files[viewer.index];

  return createPortal(
    <AnimatePresence>
      {viewer !== null && file !== undefined && (
        <motion.div
          key="viewer"
          className={styles['overlay']}
          role="dialog"
          aria-modal="true"
          aria-label={file.name}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: durations.exitBase, ease: exitEase } }}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeViewer();
            }
          }}
        >
          <header className={styles['head']}>
            <div className={styles['head__info']}>
              <span className={styles['head__name']}>{file.name}</span>
              <span className={styles['head__meta']}>
                {formatSize(file.size)}
                {file.uploader && (
                  <>
                    {' · '}
                    {t.files.uploadedBy(
                      file.uploader.name === '' ? file.uploader.email : file.uploader.name,
                    )}
                  </>
                )}
                {viewer.files.length > 1 && (
                  <>
                    {' · '}
                    {viewer.index + 1}/{viewer.files.length}
                  </>
                )}
              </span>
            </div>
            <div className={styles['head__actions']}>
              <Tooltip content={t.files.download}>
                <a
                  className={styles['action']}
                  href={fileUrl(file, true)}
                  download={file.name}
                  aria-label={t.files.download}
                >
                  <Download />
                </a>
              </Tooltip>
              <Tooltip content={t.files.open}>
                <a
                  className={styles['action']}
                  href={fileUrl(file)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t.files.open}
                >
                  <ExternalLink />
                </a>
              </Tooltip>
              {viewer.canRemove?.(file) === true && viewer.onRemove !== undefined && (
                <Tooltip content={t.files.remove}>
                  <button
                    type="button"
                    className={cx(styles['action'], styles['action--danger'])}
                    aria-label={t.files.remove}
                    onClick={() => {
                      const remove = viewer.onRemove;

                      if (remove) {
                        void remove(file).then(() => {
                          dropFromViewer(file.id);
                        });
                      }
                    }}
                  >
                    <Trash2 />
                  </button>
                </Tooltip>
              )}
              <Tooltip content={t.files.close}>
                <button
                  type="button"
                  className={styles['action']}
                  aria-label={t.files.close}
                  onClick={closeViewer}
                >
                  <X />
                </button>
              </Tooltip>
            </div>
          </header>

          {viewer.files.length > 1 && (
            <>
              <button
                type="button"
                className={cx(styles['arrow'], styles['arrow--prev'])}
                aria-label={t.files.prev}
                onClick={() => {
                  stepViewer(-1);
                }}
              >
                <ChevronLeft />
              </button>
              <button
                type="button"
                className={cx(styles['arrow'], styles['arrow--next'])}
                aria-label={t.files.next}
                onClick={() => {
                  stepViewer(1);
                }}
              >
                <ChevronRight />
              </button>
            </>
          )}

          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={file.id}
              className={styles['stage']}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96, transition: { duration: durations.exitFast } }}
              transition={springs.spring}
              onClick={(event) => {
                if (event.target === event.currentTarget) {
                  closeViewer();
                }
              }}
            >
              <Content file={file} />
            </motion.div>
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

function Content({ file }: { file: FileBody }) {
  const kind = fileKind(file);
  const url = fileUrl(file);

  switch (kind) {
    case 'image':
      return <ZoomableImage file={file} url={url} />;
    case 'video':
      return <video className={styles['media']} src={url} controls autoPlay />;
    case 'audio':
      return (
        <div className={styles['card']}>
          <FileIcon file={file} />
          <span className={styles['card__name']}>{file.name}</span>
          <audio className={styles['audio']} src={url} controls autoPlay />
        </div>
      );
    case 'pdf':
      return <iframe className={styles['frame']} src={url} title={file.name} />;
    case 'text':
      return <TextContent file={file} url={url} />;
    default:
      return (
        <div className={styles['card']}>
          <FileIcon file={file} />
          <span className={styles['card__name']}>{file.name}</span>
          <span className={styles['card__meta']}>{formatSize(file.size)}</span>
          <span className={styles['card__hint']}>{t.files.noPreview}</span>
          <a className={styles['card__download']} href={fileUrl(file, true)} download={file.name}>
            <Download />
            {t.files.download}
          </a>
        </div>
      );
  }
}

function FileIcon({ file }: { file: FileBody }) {
  return (
    <span className={styles['icon']}>
      <File />
      <span className={styles['icon__label']}>{fileLabel(file)}</span>
    </span>
  );
}

function ZoomableImage({ file, url }: { file: FileBody; url: string }) {
  const [zoomed, setZoomed] = useState(false);

  return (
    <motion.img
      className={cx(styles['image'], zoomed && styles['image--zoomed'])}
      src={url}
      alt={file.name}
      draggable={false}
      animate={{ scale: zoomed ? 1.8 : 1 }}
      transition={springs.rubber}
      title={t.files.zoom}
      onClick={() => {
        setZoomed((z) => !z);
      }}
    />
  );
}

function TextContent({ file, url }: { file: FileBody; url: string }) {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const tooBig = file.size > MAX_TEXT;

  useEffect(() => {
    let cancelled = false;

    if (tooBig) {
      return;
    }

    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((body) => {
        if (!cancelled) {
          setText(body);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url, tooBig]);

  if (failed || tooBig) {
    return (
      <div className={styles['card']}>
        <FileIcon file={file} />
        <span className={styles['card__name']}>{file.name}</span>
        <span className={styles['card__hint']}>{t.files.tooBig}</span>
      </div>
    );
  }

  return <pre className={styles['text']}>{text ?? ''}</pre>;
}
